import { io, Socket } from 'socket.io-client';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { usePkce } from '@oliasoft/pkce';
import { DefaultLogger } from '@oliasoft/logger';
import commonconf from 'client/common/utils/conf';
import { NewAppVersionContext } from 'views/navigation/new-app-version/new-app-version-provider';
import { ReleaseNotesContext } from 'views/navigation/new-app-version/release-notes-provider';
import { newVersionDetected } from 'store/entities/release-notes/release-notes';
import ClientConfig from 'src/config/config';
import { NotificationType } from 'src/enums/notifications';

const log = new DefaultLogger('Websockets');
log.setLevel('INFO');

interface ISocketContextValue {
  socket: Socket;
  isConnected: boolean;
}

export const SocketContext = createContext<ISocketContextValue | null>(null);

export const WEBSOCKET_EVENTS = {
  change_scope: 'CHANGE_SCOPE',
  notification_message: 'NOTIFICATION_MESSAGE',
};

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const { accessToken } = usePkce();
  const dispatch = useDispatch();

  const { newAppVersionModalEnabled } = useContext(NewAppVersionContext);
  const { triggerReleaseNotesFlow } = useContext(ReleaseNotesContext);
  const releaseNotesEnabled = ClientConfig.config.enableReleaseNotes;

  const hostEnv = window.__runtimeConfigStatic?.host_env;
  const wsBaseUrl =
    window.__runtimeConfigStatic?.notification_websocket_url || 'wss://notification-service.test.oliasoft.com';
  const [isConnected, setConnected] = useState(false);

  const socket = useMemo(() => {
    log.info('Token renewed. Creating a new socket instance.');

    const newSocket = io(`${wsBaseUrl}`, {
      addTrailingSlash: false,
      path: '/websockets',
      autoConnect: !!accessToken, // Only auto-connect if we have a token
      withCredentials: true,
      query: {
        hostenv: hostEnv,
      },
      auth: {
        token: accessToken, // Use the latest token in the handshake
      },
    });

    return newSocket;
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      log.info('No access token. Skipping socket setup.');
      setConnected(false);
      return;
    }

    function onConnect() {
      log.info('Authenticated and Connected!');
      setConnected(true);
    }

    function onDisconnect(reason: Socket.DisconnectReason) {
      log.info('Disconnected with reason', reason);
      setConnected(false);
    }

    function handleConnectError(err: Error) {
      log.error('Connection has not been established.', err.message);
    }

    function handleNotificationMessage(msg: any) {
      const { notification } = msg;
      const versionsNotEqual = notification.metadata?.newVersion !== commonconf.version;
      if (notification.type === NotificationType.app_new_version && versionsNotEqual) {
        const newVersion = notification.metadata?.newVersion;
        if (newVersion) {
          dispatch(newVersionDetected({ newVersion }));
        }

        if (releaseNotesEnabled) {
          triggerReleaseNotesFlow();
        } else {
          newAppVersionModalEnabled();
        }
      }
    }

    socket.on('connect', onConnect);
    socket.on(WEBSOCKET_EVENTS.notification_message, handleNotificationMessage);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', handleConnectError);

    // If a new socket was created (due to token change), ensure it connects.
    // If it was already connected, it will be disconnected by the `return` cleanup below.
    if (!socket.connected) {
      socket.connect();
    }

    // This runs when the component unmounts OR when the `socket` dependency changes (i.e., token refresh).
    return () => {
      log.info('Cleaning up old socket instance and listeners.');
      // Remove listeners from the old instance
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', handleConnectError);
      socket.off(WEBSOCKET_EVENTS.notification_message, handleNotificationMessage);

      // Manually close the old socket connection when a new one is created
      // or when the component unmounts to prevent background connections.
      socket.close();
      setConnected(false);
    };
  }, [socket, accessToken]);

  return <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>;
};

export const useSocket = () => useContext(SocketContext);
